import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { css } from "@emotion/css";

const MAX_ATTEMPTS = 20;
const RETRY_MS = 1000;

type State = { attempt: number; loaded: boolean; failed: boolean };

// Polls an image URL until it loads. When the image is freshly uploaded to S3
// there can be a short delay before CloudFront serves it, so we retry with a
// cache-busting query param for up to ~20 seconds.
const PollingImage: React.VFC<{ src: string; className?: string; alt?: string }> = ({ src, className, alt = "" }) => {
  const [state, setState] = useState<State>({ attempt: 0, loaded: false, failed: false });
  const timer = useRef<number | null>(null);

  useEffect(() => {
    setState({ attempt: 0, loaded: false, failed: false });
    return () => {
      if (timer.current != null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [src]);

  const { attempt, loaded, failed } = state;
  const busted = attempt === 0 ? src : `${src}${src.includes("?") ? "&" : "?"}cb=${attempt}`;

  return (
    <Box className={styles.slot}>
      {!loaded && !failed && <CircularProgress size={16} />}
      <img
        src={busted}
        alt={alt}
        className={className}
        style={{ display: loaded ? undefined : "none" }}
        onLoad={() => setState(s => (s.loaded ? s : { ...s, loaded: true }))}
        onError={() => {
          setState(s => {
            if (s.attempt >= MAX_ATTEMPTS) return { ...s, failed: true };
            timer.current = window.setTimeout(() => setState(cur => ({ ...cur, attempt: cur.attempt + 1 })), RETRY_MS);
            return s;
          });
        }}
      />
      {failed && (
        <Typography variant="caption" color="error">
          Preview unavailable
        </Typography>
      )}
    </Box>
  );
};

export default PollingImage;

const styles = {
  slot: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 60px;
    min-height: 60px;
  `,
};
